/**
 * Where a signed-in user's role comes from.
 *
 * Previously this was a hardcoded list of admin email addresses sitting in the
 * public JS bundle. That did not grant any data access -- AppSync enforces the
 * Cognito `admin` / `manager` groups server-side -- but it meant the UI and the
 * API disagreed about who was staff: adding someone to the Cognito group gave
 * them no admin screens, and removing them left the screens in place.
 *
 * The ID token is signed by Cognito and carries `cognito:groups`, which is the
 * exact claim AppSync checks. Reading it here makes the UI agree with the API by
 * construction.
 */
import { fetchAuthSession } from 'aws-amplify/auth';

// Cognito group -> app role, most privileged first.
const GROUP_ROLES = [
  ['admin', 'admin'],
  ['manager', 'manager'],
  ['tree_specialist', 'tree_specialist'],
  ['crop_specialist', 'crop_specialist'],
  ['home_worker', 'home_worker'],
];

export const STAFF_ROLES = ['admin', 'manager'];

/** Groups from the signed ID token. Empty array if not signed in. */
export const getGroups = async () => {
  try {
    const session = await fetchAuthSession();
    const claim = session?.tokens?.idToken?.payload?.['cognito:groups'];
    return Array.isArray(claim) ? claim : (claim ? [claim] : []);
  } catch {
    return [];
  }
};

/**
 * Resolve role + groups for the current session.
 * Anyone not in a group is a customer -- never assume staff.
 */
export const resolveRole = async () => {
  const groups = await getGroups();
  const match = GROUP_ROLES.find(([group]) => groups.includes(group));
  return { role: match ? match[1] : 'customer', groups };
};

export const isStaff = (role) => STAFF_ROLES.includes(role);
